import React, { useState } from 'react'
import {
  Button,
  ButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@material-ui/core'
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { followLink, FollowLinkTarget, resolveFollowLinkTarget } from './follow-link'

export type ShareMenuOption = {
  id: string
  label: string
  icon: React.ReactElement
  href?: string
  target?: FollowLinkTarget
  onSelect?: () => void
  disabled?: boolean
}

export type ShareMenuProps = {
  options: ShareMenuOption[]
  label: string
  startIcon?: React.ReactElement
  endIcon?: React.ReactElement
  buttonProps?: ButtonProps
  menuId?: string
}

const defaultMenuId = 'share-menu'

const handleOptionSelect = (
  option: ShareMenuOption,
  onClose: () => void
): void => {
  onClose()
  option.onSelect?.()

  if (!option.href) {
    return
  }

  const target = resolveFollowLinkTarget(option.href, option.target)
  followLink(option.href, target)
}

/** Dropdown action menu: email, external links, or custom handlers via `options`. */
export const ShareMenu: React.FC<ShareMenuProps> = ({
  options,
  label,
  startIcon = <ShareOutlinedIcon fontSize="small" />,
  endIcon = <ArrowDropDownIcon fontSize="small" />,
  buttonProps,
  menuId = defaultMenuId,
}): React.ReactElement | null => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  if (!options.length) {
    return null
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        size="small"
        color="default"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={startIcon}
        endIcon={endIcon}
        {...buttonProps}
      >
        {label}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted
        getContentAnchorEl={null}
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            disabled={option.disabled}
            onClick={() => handleOptionSelect(option, handleClose)}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
